<template>
  <div class="v2026-phdr">
    <div class="v2026-phdr__avatar-wrap">
      <img class="v2026-phdr__avatar" :src="avatarUrl" alt="" @error="onAvatarError">
      <img v-if="channelBadge" class="v2026-phdr__badge"
           :src="channelBadge.icon" :alt="channelBadge.alt">
    </div>
    <div class="v2026-phdr__text">
      <div ref="nameRow" class="v2026-phdr__name-row">
        <!-- Group badge first, before the name: strongest signal at the start of the reading
             line, and the same order legacy used (CustomerInformation.vue:41-46). The letter
             itself is the signal; colour only reinforces it. -->
        <span v-if="groupBadge" class="v2026-phdr__grp" :class="'is-' + groupBadge.tone"
              :title="groupBadge.title" :aria-label="groupBadge.title">{{ groupBadge.label }}</span>
        <!-- canEdit=false means there is no record to write to, so the affordance is dropped
             rather than shown and failing. -->
        <template v-if="!canEdit || !addLabel || displayName">
          <span class="v2026-phdr__name"><span v-for="item in statusIcons" :key="item.id" class="v2026-phdr__si" :style="{ color: item.color }" :title="item.label"><span v-if="item.badgeText" class="v2026-phdr__si-badge"><span class="v2026-phdr__si-badge-t">{{ item.badgeText }}</span></span><template v-else><i v-for="n in (item.repeat || 1)" :key="n" :class="item.fa" aria-hidden="true"></i></template></span>{{ displayName }}</span>
          <div v-if="compactActions && (copyText || canEdit)" class="v2026-phdr__more"
               @keydown.esc="moreOpen = false">
            <button ref="editBtn" type="button" class="v2026-phdr__icon-btn"
                    aria-label="ตัวเลือกเพิ่มเติม" title="ตัวเลือกเพิ่มเติม" aria-haspopup="menu"
                    :aria-expanded="moreOpen ? 'true' : 'false'"
                    @click="moreOpen = !moreOpen">
              <span class="v2026-phdr__copy-ico" aria-hidden="true"><i class="fas fa-ellipsis"></i></span>
            </button>
            <OverlayCard v-if="moreOpen" class="v2026-phdr__menu" role="menu"
                         :open="moreOpen" :ignore="$refs.editBtn" @close="moreOpen = false">
              <button v-if="copyText" type="button" class="v2026-phdr__menu-item" role="menuitem"
                      @click="moreOpen = false; copyName()">
                <span class="v2026-phdr__copy-ico" aria-hidden="true"><i class="far fa-copy"></i></span>
                <span>คัดลอกชื่อ</span>
              </button>
              <button v-if="canEdit" type="button" class="v2026-phdr__menu-item" role="menuitem"
                      @click="moreOpen = false; openEditName()">
                <span v-if="registry.editIconMask" class="v2026-phdr__mask v2026-phdr__edit"
                      aria-hidden="true" :style="maskStyle(registry.editIcon)"></span>
                <img v-else class="v2026-phdr__edit-img" aria-hidden="true" :src="registry.editIcon" alt="">
                <span>แก้ไขชื่อ</span>
              </button>
            </OverlayCard>
          </div>
          <button v-if="!compactActions && copyText" type="button" class="v2026-phdr__icon-btn v2026-phdr__copy"
                  :aria-label="copied ? 'คัดลอกแล้ว' : 'คัดลอกชื่อ'" :title="copied ? 'คัดลอกแล้ว' : 'คัดลอกชื่อ'"
                  @click="copyName">
            <span v-if="copied" key="copied" class="v2026-phdr__copy-ico is-copied" aria-hidden="true"><i class="fas fa-check"></i></span>
            <span v-else key="copy" class="v2026-phdr__copy-ico" aria-hidden="true"><i class="far fa-copy"></i></span>
          </button>
          <button v-if="!compactActions && canEdit" ref="editBtn" type="button" class="v2026-phdr__icon-btn"
                  aria-label="แก้ไขชื่อ" aria-haspopup="dialog" @click="openEditName()">
            <span v-if="registry.editIconMask" class="v2026-phdr__mask v2026-phdr__edit"
                  aria-hidden="true" :style="maskStyle(registry.editIcon)"></span>
            <img v-else class="v2026-phdr__edit-img" aria-hidden="true" :src="registry.editIcon" alt="">
          </button>
        </template>
        <button v-else ref="editBtn" type="button" class="v2026-phdr__add"
                :aria-label="addLabel" aria-haspopup="dialog" @click="openEditName()">
          <span class="v2026-phdr__add-ico" aria-hidden="true" :style="maskStyle(plusIcon)"></span>
          <span class="v2026-phdr__add-text">{{ addLabel }}</span>
        </button>
      </div>
      <div class="v2026-phdr__sub">
        <span class="v2026-phdr__sub-label">{{ roleLabel }}</span>
    <!-- STRICT !== null, not truthiness: an empty string is a real "no province" answer. -->
        <template v-if="subLink !== null">
          <span class="v2026-phdr__dot">•</span>
          <a role="link" class="v2026-phdr__link" href="javascript:void(0)">{{ subLink }}</a>
        </template>
        <!-- Legacy's admin deep link (CustomerInformation.vue:80-87), restored above the fold.
             It deliberately DUPLICATES the EntityCard's "User ID:" lower in the panel: different
             data source (conversation payload vs searchCustomer result), so the two can legitimately
             differ. Do not collapse them into one computed. See uxui-spec §3.5. -->
        <template v-if="hasBeneatUserId">
          <span class="v2026-phdr__dot">•</span>
          <span class="v2026-phdr__uid">
            <span class="v2026-phdr__sub-label">User Id: </span>
            <!-- No href means no anchor: a dead link is worse than plain text. -->
            <a v-if="beneatUserHref" class="v2026-phdr__link" :href="beneatUserHref"
               target="_blank" rel="noopener noreferrer">{{ beneatUserId }}</a>
            <span v-else class="v2026-phdr__sub-label">{{ beneatUserId }}</span>
          </span>
        </template>
      </div>
    </div>

    <BaseModal :open="showEditName" max-width="480px" :position="['top', 'center']"
               :modalStyle="['margin-top:10%']" @close="closeEditName()">
      <div class="v2026-phdr-en" role="dialog" aria-modal="true"
           aria-labelledby="v2026-phdr-en-title">
        <div class="v2026-phdr-en__head">
          <span id="v2026-phdr-en-title" class="v2026-phdr-en__title">{{ registry.modalTitle }}</span>
          <button type="button" class="v2026-phdr-en__close" aria-label="ปิด"
                  @click="closeEditName()">×</button>
        </div>
        <div class="v2026-phdr-en__body">
          <label class="v2026-phdr-en__label" for="v2026-phdr-en-input">ชื่อ</label>
          <div class="v2026-phdr-en__row">
            <div class="v2026-phdr-en__field">
              <input id="v2026-phdr-en-input" type="text" class="v2026-phdr-en__input"
                     :maxlength="maxlengthName" v-model="editNameDraft" placeholder="ชื่อ-สกุล"
                     @keyup.enter="saveEditName">
              <span class="v2026-phdr-en__counter">{{ (editNameDraft || '').length }}/{{ maxlengthName }}</span>
            </div>
            <button type="button" class="v2026-phdr-en__save"
                    :disabled="!(editNameDraft || '').trim() || (registry.inlineSaveFeedback && saving)"
                    @click="saveEditName">บันทึก</button>
          </div>
          <p class="v2026-phdr-en__hint">{{ registry.nameHint }}</p>
          <p v-if="registry.inlineSaveFeedback && saveError" class="v2026-phdr-en__error"
             role="alert">* บันทึกไม่สำเร็จ กรุณาลองใหม่</p>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BaseModal from '@/components/coversation/BaseModal.vue'
import OverlayCard from '@/components/coversation/OverlayCard.vue'

// Name row narrower than this: copy / edit fold into the ⋯ menu so the name keeps its room.
const COMPACT_ACTIONS_WIDTH = 220

// Per-panel differences, and the ONLY ones:
// 1. Pencil glyph — one panel's asset is stroke-based (a CSS mask would paint it a solid
//    square), the other is a solid glyph painted through a mask. Not interchangeable.
// 2. Wording. "ผู้ใช้" means the customer in this product; a maid chat says "คุณแม่บ้าน".
// 3. inlineSaveFeedback — the professional panel has no toast, so without an inline line a
//    failed save would give ZERO signal and read as a frozen UI.
const TYPE_REGISTRY = {
  0: {
    name: 'customer',
    editIcon: require('@/assets/icons2026/pencil-edit.svg'),
    editIconMask: false,
    modalTitle: 'ชื่อผู้ใช้',
    nameHint: '* กรุณาใช้ชื่อที่บ่งบอกถึงคุณลูกค้าอย่างชัดเจน',
    inlineSaveFeedback: false
  },
  1: {
    name: 'professional',
    editIcon: require('@/assets/icons2026/edit.svg'),
    editIconMask: true,
    modalTitle: 'ชื่อคุณแม่บ้าน',
    nameHint: '* กรุณาใช้ชื่อที่บ่งบอกถึงคุณแม่บ้านอย่างชัดเจน',
    inlineSaveFeedback: true
  },
  // Applicant: same silent-panel treatment as the maid — no toast sink.
  2: {
    name: 'applicant',
    editIcon: require('@/assets/icons2026/edit.svg'),
    editIconMask: true,
    modalTitle: 'ชื่อผู้สมัคร',
    nameHint: '* กรุณาใช้ชื่อที่บ่งบอกถึงผู้สมัครอย่างชัดเจน',
    inlineSaveFeedback: true
  }
}

const PLACEHOLDER_AVATAR = 'https://beneat.co/static/img/unknown.png'

// The professional-group letters legacy showed in the centre column (CustomerInformation.vue:41-46).
// Keys are professional_group_id. An id that is not listed renders NO badge — same as legacy, whose
// v-else-if chain simply ran out. Tones map onto the .v2026-phdr__grp recipe, never bootstrap's
// badge-* classes.
const PROFESSIONAL_GROUP_BADGES = {
  0: { label: '0', tone: 'neutral' },
  1: { label: 'A', tone: 'success' },
  2: { label: 'B', tone: 'warning' },
  3: { label: 'C', tone: 'warning' },
  4: { label: 'H', tone: 'info' }
}

export default {
  name: 'panel-header-v2026',
  components: {
    BaseModal,
    OverlayCard
  },
  props: {
    type: {
      type: Number,
      default: 0
    },
    avatarUrl: {
      type: String,
      default: ''
    },
    channelBadge: {
      type: Object,
      default: null
    },
    displayName: {
      type: String,
      default: '-'
    },
    // The conversation's status icons (catalogue entries from resolveStatusIcons), drawn before the
    // name like the chat card. Display only; never part of the name the edit modal saves.
    statusIcons: {
      type: Array,
      default: () => []
    },
    roleLabel: {
      type: String,
      default: ''
    },
    subLink: {
      type: String,
      default: null
    },
    convCustomer: {
      type: Object,
      default: null
    },
    conversationId: {
      type: [Number, String],
      default: null
    },
    addLabel: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    // The four below are ADDITIVE and every default is the OFF state, so a call site that passes
    // none of them renders byte-identically to before. The default IS the flag — no boolean needed.
    customerType: {
      type: String,
      default: ''
    },
    professional: {
      type: Object,
      default: null
    },
    beneatUserId: {
      type: [Number, String],
      default: null
    },
    beneatUserHref: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      editNameDraft: '', // Modal A draft (seeded on open, discarded on close)
      showEditName: false, // Modal A visibility
      saving: false, // request in flight → M3 disables บันทึก (type 1 only)
      saveError: false, // M1 inline danger line (type 1 only)
      plusIcon: require('@/assets/icons2026/plus.svg'),
      copied: false,
      copiedTimer: null,
      compactActions: false,
      moreOpen: false,
      nameRowObserver: null
    }
  },
  computed: {
    // Status emoji glued to the name, the way admins wrote names in the legacy list.
    copyText () {
      const name = (this.displayName || '').trim()
      if (!name || name === '-') return ''
      return this.statusIcons.map(item => item.emoji || '').join('') + name
    },
    maxlengthName () {
      return 100
    },
    registry () {
      return TYPE_REGISTRY[this.type] || TYPE_REGISTRY[0]
    },
    // Both conditions must hold, exactly as legacy: the maid rail AND a beneat_professional record.
    // status === 0 WINS over any group id — that is legacy's v-if/v-else-if precedence and it must
    // not be reordered into a switch on the group id, or a suspended maid in group A reads as "A".
    groupBadge () {
      if (this.customerType !== 'beneat_professional') return null
      const pro = this.professional
      if (!pro) return null
      if (pro.status === 0) {
        return { label: 'โดนปลด', tone: 'danger', title: 'สถานะ: โดนปลด' }
      }
      const entry = PROFESSIONAL_GROUP_BADGES[pro.professional_group_id]
      if (!entry) return null
      return { label: entry.label, tone: entry.tone, title: 'กลุ่มแม่บ้าน: ' + entry.label }
    },
    hasBeneatUserId () {
      return this.beneatUserId !== null && this.beneatUserId !== undefined && this.beneatUserId !== ''
    }
  },
  watch: {
    convCustomer: 'resetModalState',
    conversationId () {
      this.resetModalState()
      this.clearCopied()
      this.moreOpen = false
    }
  },
  mounted () {
    const row = this.$refs.nameRow
    if (row && typeof ResizeObserver !== 'undefined') {
      this.nameRowObserver = new ResizeObserver(entries => {
        const width = entries[0] ? entries[0].contentRect.width : row.clientWidth
        this.compactActions = width > 0 && width < COMPACT_ACTIONS_WIDTH
        if (!this.compactActions) this.moreOpen = false
      })
      this.nameRowObserver.observe(row)
    }
  },
  beforeDestroy () {
    this.clearCopied()
    if (this.nameRowObserver) this.nameRowObserver.disconnect()
  },
  methods: {
    async copyName () {
      const text = this.copyText
      if (!text) return
      let ok = true
      try {
        await navigator.clipboard.writeText(text)
      } catch (e) {
        const area = document.createElement('textarea')
        area.value = text
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.select()
        ok = document.execCommand('copy')
        document.body.removeChild(area)
      }
      if (!ok) {
        this.$emit('notify', { variant: 'danger', title: 'คัดลอกไม่สำเร็จ', message: 'กรุณาคัดลอกด้วยตนเอง' })
        return
      }
      this.$emit('notify', { variant: 'success', title: 'คัดลอกชื่อแล้ว', message: text })
      this.clearCopied()
      this.copied = true
      this.copiedTimer = setTimeout(this.clearCopied, 1500)
    },
    clearCopied () {
      if (this.copiedTimer) {
        clearTimeout(this.copiedTimer)
        this.copiedTimer = null
      }
      this.copied = false
    },
    ...mapActions('customer', ['saveCustomer']),
    ...mapActions('conversation', ['searchCustomer']),
    openEditName () {
      this.editNameDraft = (this.convCustomer && this.convCustomer.name) ? this.convCustomer.name : ''
      this.saveError = false
      this.showEditName = true
    },
    closeEditName () {
      this.showEditName = false
      this.$nextTick(() => {
        if (this.$refs.editBtn && this.$refs.editBtn.focus) this.$refs.editBtn.focus()
      })
    },
    resetModalState () {
      this.showEditName = false
      this.editNameDraft = ''
      this.saving = false
      this.saveError = false
    },
    async saveEditName () {
      const name = (this.editNameDraft || '').trim()
      if (!name) { this.$swal({ text: 'กรุณากรอกชื่อ' }); return }
      const c = this.convCustomer
      this.saving = true
      this.saveError = false
      try {
        await this.saveCustomer({
          customer: { id: c.id, name, phone: c.phone, email: c.email },
          conversation_id: this.conversationId
        })
        c.name = name
        await this.searchCustomer({
          phone: c.phone,
          email: c.email,
          line_user_id: c.lid,
          beneat_professional_id: c.beneat_professional_id
        })
        this.closeEditName()
        this.$emit('notify', { variant: 'success', title: 'บันทึกข้อมูลสำเร็จ', message: 'ทำการบันทึกข้อมูลสำเร็จ' })
      } catch (e) {
        this.saveError = true
        this.$emit('notify', { variant: 'danger', title: 'เกิดข้อผิดพลาด', message: 'บันทึกไม่สำเร็จ กรุณาลองใหม่' })
      } finally {
        this.saving = false
      }
    },
    onAvatarError (e) {
      if (e && e.target && e.target.src !== PLACEHOLDER_AVATAR) {
        e.target.src = PLACEHOLDER_AVATAR
      }
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-phdr {
  --v2026-phdr-link: #007bff;
  --v2026-phdr-surface: #ffffff;
  --v2026-phdr-surface-hover: #e9ecf1;
  --v2026-phdr-text: #101828;
  --v2026-phdr-text-secondary: #667085;
  --v2026-phdr-text-muted: #98a2b3;
  --v2026-phdr-icon: #475467;
  --v2026-phdr-main: #31C1D7;

  /* Group-badge tones. v2026 had NO text-badge primitive (ActionChip is an action chip,
     ServiceBadges are icons, .v2026-tag-chip is 36px tall), so this recipe is new — derived from
     the existing chip silhouette and the panel palette. Every fg/bg pair is >= 4.5:1.
     `info` is the darkened form of --v2026-phdr-main; `neutral` reuses the secondary/hover values
     verbatim. Declared on the component root, which is this codebase's token convention. */
  --v2026-phdr-grp-danger-fg:  #B42318;
  --v2026-phdr-grp-danger-bg:  #FEF3F2;
  --v2026-phdr-grp-danger-bd:  #FDA29B;
  --v2026-phdr-grp-success-fg: #027A48;
  --v2026-phdr-grp-success-bg: #ECFDF3;
  --v2026-phdr-grp-success-bd: #ABEFC6;
  --v2026-phdr-grp-warning-fg: #B54708;
  --v2026-phdr-grp-warning-bg: #FFFAEB;
  --v2026-phdr-grp-warning-bd: #FEDF89;
  --v2026-phdr-grp-info-fg:    #0E7C8C;
  --v2026-phdr-grp-info-bg:    #E7F8FB;
  --v2026-phdr-grp-info-bd:    #9FE3EE;
  --v2026-phdr-grp-neutral-fg: #475467;
  --v2026-phdr-grp-neutral-bg: #F2F4F7;
  --v2026-phdr-grp-neutral-bd: #E4E7EC;

  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0 16px;
}
.v2026-phdr__avatar-wrap {
  position: relative;
  flex: 0 0 auto;
}
.v2026-phdr__avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
}
.v2026-phdr__badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid var(--v2026-phdr-surface);
  background: var(--v2026-phdr-surface);
}
.v2026-phdr__text {
  flex: 1;
  min-width: 0;
}
.v2026-phdr__name-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}
.v2026-phdr__name {
  flex: 0 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 16px;
  font-weight: 700;
  color: var(--v2026-phdr-text);
}
.v2026-phdr__name-row > .v2026-phdr__icon-btn,
.v2026-phdr__more {
  flex: 0 0 auto;
}
.v2026-phdr__more {
  position: relative;
}
.v2026-phdr__more .v2026-phdr__menu {
  top: calc(100% + 4px);
  right: 0;
  min-width: 160px;
  padding: 4px;
}
.v2026-phdr__menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 14px;
  color: var(--v2026-phdr-text);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}
.v2026-phdr__menu-item:hover,
.v2026-phdr__menu-item:focus-visible {
  outline: none;
  background: var(--v2026-phdr-surface-hover);
}
/* Mirrors ChatCardV2026's .v2026-si-mark / --inline / __badge so both places draw the same icon. */
.v2026-phdr__si {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  font-size: 13px;
  line-height: 1;
  vertical-align: middle;
  margin-right: 3px;
  position: relative;
  top: -1px;
}
.v2026-phdr__si-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 11px;
  padding: 0 3px;
  border-radius: 3px;
  background: currentColor;
}
.v2026-phdr__si-badge-t {
  color: #ffffff;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.2px;
}
/* Additive: with a third segment a 320px panel would clip the id. Wrapping is additive because
   the one- and two-segment cases still fit on a single line and are unaffected. */
.v2026-phdr__sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  font-size: 14px;
}
.v2026-phdr__sub-label {
  color: var(--v2026-phdr-text-secondary);
}
/* The label and the id never break apart from each other; the row breaks before them instead. */
.v2026-phdr__uid {
  white-space: nowrap;
}

.v2026-phdr__grp {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto; /* never shrinks the name */
  height: 20px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
}
.v2026-phdr__grp.is-danger {
  color: var(--v2026-phdr-grp-danger-fg);
  background: var(--v2026-phdr-grp-danger-bg);
  border-color: var(--v2026-phdr-grp-danger-bd);
}
.v2026-phdr__grp.is-success {
  color: var(--v2026-phdr-grp-success-fg);
  background: var(--v2026-phdr-grp-success-bg);
  border-color: var(--v2026-phdr-grp-success-bd);
}
.v2026-phdr__grp.is-warning {
  color: var(--v2026-phdr-grp-warning-fg);
  background: var(--v2026-phdr-grp-warning-bg);
  border-color: var(--v2026-phdr-grp-warning-bd);
}
.v2026-phdr__grp.is-info {
  color: var(--v2026-phdr-grp-info-fg);
  background: var(--v2026-phdr-grp-info-bg);
  border-color: var(--v2026-phdr-grp-info-bd);
}
.v2026-phdr__grp.is-neutral {
  color: var(--v2026-phdr-grp-neutral-fg);
  background: var(--v2026-phdr-grp-neutral-bg);
  border-color: var(--v2026-phdr-grp-neutral-bd);
}

.v2026-phdr__dot {
  color: var(--v2026-phdr-text-muted);
}

.v2026-phdr__link {
  color: var(--v2026-phdr-link);
  text-decoration: none;
  font-size: 14px;
}
.v2026-phdr__link:hover {
  text-decoration: underline;
}
.v2026-phdr__link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 3px;
}

.v2026-phdr__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-phdr__icon-btn:hover {
  background: var(--v2026-phdr-surface-hover);
}
.v2026-phdr__icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-phdr__mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-phdr-icon);
}
.v2026-phdr__edit {
  width: 16px;
  height: 16px;
  background-color: var(--v2026-phdr-icon);
}

.v2026-phdr__copy-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 14px;
  line-height: 1;
  color: var(--v2026-phdr-icon);
}
.v2026-phdr__copy-ico.is-copied {
  color: #12b76a;
}

.v2026-phdr__edit-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
}

.v2026-phdr__add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px 0 6px;
  border: 0;
  background: transparent;
  border-radius: 999px;
  color: var(--v2026-phdr-main);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.v2026-phdr__add:hover {
  background: rgba(49, 193, 215, 0.12);
}
.v2026-phdr__add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-phdr__add-ico {
  display: inline-block;
  width: 14px;
  height: 14px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-phdr-en__head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  background: #FFFFFF;
  border-bottom: 1px solid #EBEDF0;
  padding: 16px 24px;
}
.v2026-phdr-en__body {
  padding: 20px 24px;
}
.v2026-phdr-en__title {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}
.v2026-phdr-en__close {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 20px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-phdr-en__label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #66696C;
}
.v2026-phdr-en__row {
  display: flex;
  align-items: stretch;
  gap: 12px;
}
.v2026-phdr-en__field {
  position: relative;
  flex: 1;
}
.v2026-phdr-en__input {
  width: 100%;
  height: 44px;
  padding: 0 56px 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  color: #1F2937;
  outline: none;
}
.v2026-phdr-en__input:focus {
  border-color: var(--v2026-phdr-main);
}
.v2026-phdr-en__counter {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  font-size: 13px;
  color: #9CA3AF;
  pointer-events: none;
}
.v2026-phdr-en__save {
  height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: var(--v2026-phdr-main);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.v2026-phdr-en__save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.v2026-phdr-en__hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #9CA3AF;
}

.v2026-phdr-en__error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #F04438;
}
</style>
